import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import { DatePicker, TreeSelect } from "antd";
import dayjs from "dayjs";

// Helpers
import CommonSelect from "../../../../core/common/commonSelect";
import { notify } from "../../../../services/toast.config";
import api from "../../../../services/api";
import Loader from "../../../../core/common/loader";
import ImageSelectDropMenu from "../../imageSelectDropMenu";
import { getFullName } from "../../../ers/reports-settings/report-type/helpers/form.handler";

// Constants and types
interface AddAssetModalProps {
  companyOptions: any;
  formDataCompanyChoose: any;
  assetCategoryOptions: any;
  assetDepartmentOptions: any;
  assetLocationOptions: any;
  employeeOptions: any;
  assetProductOptions: any;
  statusOptions: any;
  updateUI: any;
}

type AssetFormData = {
  productId: string;
  assignDate: string;
  company_id: string;
  company_type: string;
  company_name: string;
  assignedTo: string;
  status: string;
  assetCategoryId: string;
  assigned_to_type: string;
  remarks: string | null;
};

const DEFAULT_FORM_DATA: AssetFormData = {
  productId: "",
  assignDate: "",
  company_id: "",
  company_type: "",
  company_name: "",
  assignedTo: "",
  status: "",
  assetCategoryId: "",
  assigned_to_type: "user",
  remarks: null,
};

const ASSIGNMENT_TYPE_OPTIONS = [
  { value: "user", label: "Employee" },
  { value: "location", label: "Location" },
  { value: "department", label: "Department" },
];

interface CategoryNode {
  id: string;
  name: string;
  status: string;
  category_level: number;
  parentId?: string | null;
  subcategories?: CategoryNode[];
}

const AssignNewAssetModal: React.FC<AddAssetModalProps> = ({
  companyOptions,
  formDataCompanyChoose,
  assetCategoryOptions,
  assetDepartmentOptions,
  assetLocationOptions,
  employeeOptions,
  assetProductOptions,
  statusOptions,
  updateUI,
}) => {
  // Local Storage
  const signature = localStorage.getItem("signature");

  // State: UI
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const [filteredProductOptions, setFilteredProductOptions] = useState<any[]>(
    []
  );
  const [categoryBreadcrumbPath, setCategoryBreadcrumbPath] =
    useState<string>("");

  // Memoized category tree data for TreeSelect
  const categoryTreeData = useMemo(() => {
    const convertCategoriesToTreeFormat = (
      categories: CategoryNode[]
    ): any[] => {
      if (!categories || !Array.isArray(categories)) {
        return [];
      }

      return categories
        .filter((category) => category && category.status === "Active")
        .map((category) => {
          const hasChildCategories =
            category.subcategories && category.subcategories.length > 0;

          return {
            title: category.name,
            value: category.id,
            key: category.id,
            children: hasChildCategories
              ? convertCategoriesToTreeFormat(category.subcategories!)
              : undefined,
          };
        });
    };

    return convertCategoriesToTreeFormat(assetCategoryOptions || []);
  }, [assetCategoryOptions]);

  // Memoized department options for CommonSelect
  const departmentSelectOptions = useMemo(() => {
    return assetDepartmentOptions.map((dept: any) => ({
      label: dept.Department,
      value: dept.key.toString(),
    }));
  }, [assetDepartmentOptions]);

  // Memoized location options for ImageSelectDropMenu
  const locationSelectOptions = useMemo(() => {
    return assetLocationOptions.map((location: any) => ({
      label: location.location_name,
      value: location.id.toString(),
      imageUrl: null,
    }));
  }, [assetLocationOptions]);

  // Memoized employee options for ImageSelectDropMenu
  const employeeSelectOptions = useMemo(() => {
    return employeeOptions.map((user: any) => ({
      label: `${getFullName(user?.salutation, user?.fname, user?.lname)} - [${
        user?.emp_id || "N/F"
      }]`,
      value: user?.id?.toString(),
      imageUrl: user?.profile_pic || null,
    }));
  }, [employeeOptions]);

  // Filter products based on selected category or show all products
  useEffect(() => {
    if (!assetProductOptions || assetProductOptions.length === 0) {
      setFilteredProductOptions([]);
      return;
    }
    let filteredProducts = assetProductOptions;
    if (selectedCategoryId && selectedCategoryId !== "") {
      filteredProducts = filteredProducts.filter((item: any) => {
        return item?.category?.path?.some(
          (pathItem: any) =>
            pathItem.id.toString() === selectedCategoryId.toString()
        );
      });
    }
    const productOptions = filteredProducts.map((product: any) => ({
      label: `${product.name} [${product?.serial_no}] ${product?.model}`,
      value: product?.id.toString(),
      imageUrl: product?.image_url_object?.url || null,
      category_id: product?.asset_category_id,
    }));

    setFilteredProductOptions(productOptions);
  }, [selectedCategoryId, assetProductOptions]);

  // Validation helpers
  const validateForm = (): boolean => {
    const requiredFields = [
      { key: "productId", label: "Product" },
      { key: "assigned_to_type", label: "Assignment Type" },
      { key: "assignedTo", label: "Assigned To" },
      { key: "assignDate", label: "Assigned Date" },
      { key: "status", label: "Status" },
    ];

    const errors: string[] = [];
    const invalid = new Set<string>();

    requiredFields.forEach(({ key, label }) => {
      if (!formData[key as keyof typeof formData]) {
        errors.push(`${label} is required`);
        invalid.add(key);
      }
    });

    setInvalidFields(invalid);
    return errors.length === 0;
  };

  const clearFieldError = (fieldName: string) => {
    setInvalidFields((prev) => {
      const newSet = new Set(prev);
      newSet.delete(fieldName);
      return newSet;
    });
  };

  const resetFormAndValidation = () => {
    setFormData({
      ...DEFAULT_FORM_DATA,
      company_id: formDataCompanyChoose.company_id,
      company_name: formDataCompanyChoose.company_name,
      company_type: formDataCompanyChoose.company_type,
    });
    setSelectedCategoryId("");
    setInvalidFields(new Set());
    setCategoryBreadcrumbPath("");
  };

  // Event handlers
  const handleCategoryChange = (categoryValue: string) => {
    setSelectedCategoryId(categoryValue);
    clearFieldError("assetCategoryId");

    // Generate and set the full breadcrumb path
    const breadcrumbPath = generateCategoryBreadcrumb(categoryValue);
    setCategoryBreadcrumbPath(breadcrumbPath);

    setFormData((prev) => ({
      ...prev,
      productId: "",
      assetCategoryId: categoryValue,
    }));
    clearFieldError("productId");
  };

  const handleSelectChange = (fieldName: string, selectedOption: any) => {
    clearFieldError(fieldName);
    setFormData((prev) => ({
      ...prev,
      [fieldName]: selectedOption || "",
    }));
  };

  const handleProductChange = (selectedProduct: any) => {
    clearFieldError("productId");
    setFormData((prev) => ({
      ...prev,
      productId: selectedProduct?.value || "",
      assetCategoryId: selectedProduct?.category_id,
    }));
  };

  const handleAssignmentTargetChange = (selectedTarget: any) => {
    clearFieldError("assignedTo");
    setFormData((prev) => ({
      ...prev,
      assignedTo: selectedTarget?.value || "",
    }));
  };

  const handleDateChange = (date: any) => {
    clearFieldError("assignDate");
    const formattedDate = date ? dayjs(date).format("YYYY-MM-DD") : "";
    setFormData((prev) => ({
      ...prev,
      assignDate: formattedDate,
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submissionData = {
      signature,
      product_id: parseInt(formData.productId),
      status: formData.status.toLowerCase() === "active" ? 1 : 0,
      company_id: formDataCompanyChoose.company_id,
      assignedTo: parseInt(formData.assignedTo),
      assigned_date: formData.assignDate,
      company_type: formDataCompanyChoose.company_type,
      asset_category_id: formData.assetCategoryId,
      assigned_to_type: formData.assigned_to_type,
      notes: formData.remarks,
    };

    try {
      setIsSubmitting(true);
      setIsLoading(true);
      const response = await api.post(
        "assetService/assignAssetToEmployee",
        submissionData
      );
      resetFormAndValidation();
      notify.success(response.data.message?.[0]?.msg || "Asset Assigned");

      // Close modal
      const dismissButton = document.querySelector(
        "#assign-asset-dismiss-modal-btn-1"
      ) as HTMLButtonElement;
      if (dismissButton) {
        dismissButton.click();
      }
      await updateUI();
    } catch (error: any) {
      notify.error(error?.data?.message?.[0]?.msg || "An error occurred.");
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  // Generate category breadcrumb path for display
  const generateCategoryBreadcrumb = useCallback(
    (
      categoryId: string,
      categories: CategoryNode[] = assetCategoryOptions
    ): string => {
      const findCategoryPathRecursive = (
        cats: CategoryNode[],
        targetId: string,
        currentPath: string[] = []
      ): string[] | null => {
        for (const cat of cats) {
          const newPath = [...currentPath, cat.name];

          if (cat.id === targetId) {
            return newPath;
          }

          if (cat.subcategories && cat.subcategories.length > 0) {
            const foundPath = findCategoryPathRecursive(
              cat.subcategories,
              targetId,
              newPath
            );
            if (foundPath) {
              return foundPath;
            }
          }
        }
        return null;
      };

      const pathArray = findCategoryPathRecursive(categories, categoryId);
      return pathArray ? pathArray.join(" → ") : "";
    },
    [assetCategoryOptions]
  );

  // Effects
  useEffect(() => {
    resetFormAndValidation();
  }, [formDataCompanyChoose]);

  // Render assignment target options based on type
  const renderAssignmentTargetField = () => {
    const { assigned_to_type } = formData;
    const invalidClass = invalidFields.has("assignedTo")
      ? "border border-danger rounded"
      : "";

    switch (assigned_to_type) {
      case "user":
        return (
          <div className={invalidClass}>
            <ImageSelectDropMenu
              key={`${selectedCategoryId}_${formData.productId}_employee`}
              options={employeeSelectOptions}
              onChange={handleAssignmentTargetChange}
              placeHolder="Select Employee"
            />
          </div>
        );

      case "department":
        return (
          <div className={invalidClass}>
            <CommonSelect
              className="select"
              options={departmentSelectOptions}
              defaultValue={
                formData.assignedTo
                  ? {
                      value: formData.assignedTo,
                      label:
                        departmentSelectOptions.find(
                          (dept: any) => dept.value === formData.assignedTo
                        )?.label || formData.assignedTo,
                    }
                  : undefined
              }
              onChange={handleAssignmentTargetChange}
              placeHolder="Select Department"
              isDisabled={isSubmitting}
            />
          </div>
        );

      case "location":
        return (
          <div className={invalidClass}>
            <ImageSelectDropMenu
              key={`${selectedCategoryId}_${formData.productId}_location`}
              options={locationSelectOptions}
              onChange={handleAssignmentTargetChange}
              placeHolder="Select Location"
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {isLoading && <Loader />}
      <div className="modal fade" id="assign_asset">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Assign Asset</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                disabled={isSubmitting}>
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleFormSubmit}>
              <div className="modal-body pb-0">
                <div className="row">
                  {/* Company Selection */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">
                      Company <span className="text-danger">*</span>
                    </label>
                    <CommonSelect
                      className="select"
                      options={companyOptions}
                      defaultValue={{
                        value: formDataCompanyChoose.company_id,
                        label: `${formDataCompanyChoose.company_name} - ${formDataCompanyChoose.company_type}`,
                      }}
                      isDisabled={true}
                    />
                  </div>

                  {/* Category Filter with TreeSelect */}
                  <div className="col-md-8 mb-3">
                    <label className="form-label">
                      Product Category{" "}
                      <span className="text-muted">(Filter - Optional)</span>
                    </label>
                    <TreeSelect
                      showSearch
                      treeNodeFilterProp="title"
                      filterTreeNode={(input, node) => {
                        if (!input || !node) return false;
                        const inputLower = input.toLowerCase();
                        const title =
                          node.title?.toString().toLowerCase() || "";
                        return title.includes(inputLower);
                      }}
                      searchValue={undefined}
                      autoClearSearchValue={true}
                      style={{ width: "100%" }}
                      value={selectedCategoryId || undefined}
                      dropdownStyle={{ maxHeight: 400, overflow: "auto" }}
                      treeData={categoryTreeData}
                      placeholder="Select category to filter products (optional)"
                      allowClear
                      treeDefaultExpandAll={false}
                      onChange={handleCategoryChange}
                      disabled={isSubmitting}
                      getPopupContainer={(triggerNode) =>
                        triggerNode.parentElement || document.body
                      }
                    />
                    <div className="mt-1">
                      {categoryBreadcrumbPath ? (
                        <small className="text-muted">
                          <i className="ti ti-route me-1"></i>
                          Selected: <strong>{categoryBreadcrumbPath}</strong>
                        </small>
                      ) : (
                        <small className="text-muted">
                          Leave empty to show all available products
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Product Selection */}
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Select Product <span className="text-danger">*</span>
                    </label>
                    <div
                      className={
                        invalidFields.has("productId")
                          ? "border border-danger rounded"
                          : ""
                      }>
                      <ImageSelectDropMenu
                        key={`products_${selectedCategoryId}_${filteredProductOptions.length}`}
                        options={filteredProductOptions}
                        onChange={handleProductChange}
                        placeHolder={
                          filteredProductOptions.length === 0
                            ? "No products available"
                            : "Select Product"
                        }
                      />
                    </div>
                    {invalidFields.has("productId") && (
                      <div className="invalid-feedback d-block">
                        <small className="text-danger">
                          Product selection is required
                        </small>
                      </div>
                    )}
                    <small className="text-muted">
                      {selectedCategoryId
                        ? `Showing products from selected category (${filteredProductOptions.length} available)`
                        : `Showing all products (${filteredProductOptions.length} available)`}
                    </small>
                  </div>

                  {/* Assignment Type */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Assignment Type <span className="text-danger">*</span>
                    </label>
                    <CommonSelect
                      className={`select ${
                        invalidFields.has("assigned_to_type")
                          ? "border-danger"
                          : ""
                      }`}
                      options={ASSIGNMENT_TYPE_OPTIONS}
                      defaultValue={
                        formData.assigned_to_type
                          ? {
                              value: formData.assigned_to_type,
                              label:
                                ASSIGNMENT_TYPE_OPTIONS.find(
                                  (opt) =>
                                    opt.value === formData.assigned_to_type
                                )?.label || formData.assigned_to_type,
                            }
                          : undefined
                      }
                      onChange={(selected: any) =>
                        handleSelectChange("assigned_to_type", selected.value)
                      }
                      placeHolder="Select Assignment Type"
                      isDisabled={isSubmitting}
                    />
                    {invalidFields.has("assigned_to_type") && (
                      <div className="invalid-feedback d-block">
                        <small className="text-danger">
                          Assignment Type is required
                        </small>
                      </div>
                    )}
                  </div>

                  {/* Assignment Target */}
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Select{" "}
                      {formData.assigned_to_type === "user"
                        ? "Employee"
                        : formData.assigned_to_type === "location"
                        ? "Location"
                        : formData.assigned_to_type === "department"
                        ? "Department"
                        : "Target"}{" "}
                      <span className="text-danger">*</span>
                    </label>
                    {renderAssignmentTargetField()}
                    {invalidFields.has("assignedTo") && (
                      <div className="invalid-feedback d-block">
                        <small className="text-danger">
                          Assignment target is required
                        </small>
                      </div>
                    )}
                  </div>

                  {/* Assigned Date */}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">
                      Assigned Date <span className="text-danger">*</span>
                    </label>
                    <div className="input-icon-end position-relative">
                      <DatePicker
                        className={`form-control datetimepicker ${
                          invalidFields.has("assignDate") ? "border-danger" : ""
                        }`}
                        value={
                          formData.assignDate
                            ? dayjs(formData.assignDate)
                            : null
                        }
                        onChange={handleDateChange}
                        format="YYYY-MM-DD"
                        placeholder="Select date"
                        disabled={isSubmitting}
                      />
                      <span className="input-icon-addon">
                        <i className="ti ti-calendar text-gray-7" />
                      </span>
                    </div>
                    {invalidFields.has("assignDate") && (
                      <div className="invalid-feedback d-block">
                        <small className="text-danger">
                          Assigned Date is required
                        </small>
                      </div>
                    )}
                  </div>

                  {/* Status Selection */}
                  <div className="col-md-3 mb-3">
                    <label className="form-label">
                      Status <span className="text-danger">*</span>
                    </label>
                    <CommonSelect
                      className={`select ${
                        invalidFields.has("status") ? "border-danger" : ""
                      }`}
                      options={statusOptions}
                      defaultValue={
                        formData.status
                          ? {
                              value: formData.status,
                              label:
                                statusOptions.find(
                                  (opt: any) => opt.value === formData.status
                                )?.label || formData.status,
                            }
                          : undefined
                      }
                      onChange={(selected: any) =>
                        handleSelectChange("status", selected.value)
                      }
                      placeHolder="Select Status"
                      isDisabled={isSubmitting}
                    />
                    {invalidFields.has("status") && (
                      <div className="invalid-feedback d-block">
                        <small className="text-danger">
                          Status is required
                        </small>
                      </div>
                    )}
                  </div>

                  {/* Remarks */}
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Remarks <span className="text-muted">(optional)</span>
                    </label>
                    <textarea
                      className="form-control"
                      placeholder="Enter any remarks..."
                      rows={2}
                      value={formData.remarks || ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          remarks: e.target.value,
                        }))
                      }
                      disabled={isSubmitting}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="submit"
                  className="btn btn-primary me-2"
                  disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Assigning...
                    </>
                  ) : (
                    "Assign"
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                  disabled={isSubmitting}
                  onClick={resetFormAndValidation}>
                  Cancel
                </button>
                <button
                  id="assign-asset-dismiss-modal-btn-1"
                  type="button"
                  className="d-none"
                  data-bs-dismiss="modal"
                />
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AssignNewAssetModal;
